// TMOS Actions I Created Page
// Date: December 9, 2025
// Purpose: Display actions created by the current user
//
// Features:
// - List view of actions I created
// - Filtering by status, priority, due date
// - Search functionality
// - Edit and delete actions (creator only)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { actionService, ActionFilters } from '@/services/actionService';
import { useLanguage } from '@/state/language/LanguageProvider';
import { ActionCard, ViewSwitcher, CardGrid, ViewMode, FilterToolbar } from '@/components/cards';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { actionEntity } from '@/config/entities/actions';
import { useCardViewRefresh } from '@/hooks/useCardViewRefresh';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

export const ActionsICreatedPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  // View mode state - default to cards
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('i-created-actions-view-mode');
    return (saved === 'cards' || saved === 'table') ? saved as ViewMode : 'cards';
  });
  
  // Card view data state
  const [actions, setActions] = useState<any[]>([]);
  const [filteredCardData, setFilteredCardData] = useState<any[]>([]);
  const [cardLoading, setCardLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>('relevance');
  
  // Use generic card view refresh hook
  const { refreshKey, refresh } = useCardViewRefresh(viewMode);

  // Delete confirmation dialog state (shared for card and table views)
  const [actionToDelete, setActionToDelete] = useState<any | null>(null);
  const [skipDeleteConfirmation, setSkipDeleteConfirmation] = useState(false);
  
  // Save view mode preference
  useEffect(() => {
    localStorage.setItem('i-created-actions-view-mode', viewMode);
  }, [viewMode]);

  /**
   * Convert filter state to ActionFilters format (API supports single status/priority)
   */
  const getActionFilters = (): ActionFilters => {
    const actionFilters: ActionFilters = {};
    if (searchTerm) actionFilters.search = searchTerm;
    if (activeFilters.includes('pending')) actionFilters.status = 'PENDING';
    else if (activeFilters.includes('in-progress')) actionFilters.status = 'IN_PROGRESS';
    else if (activeFilters.includes('completed')) actionFilters.status = 'COMPLETED';
    const priorityFilter = activeFilters.find(f => f.startsWith('priority:'));
    if (priorityFilter) {
      const p = priorityFilter.replace('priority:', '').toUpperCase();
      if (['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].includes(p)) actionFilters.priority = p as ActionFilters['priority'];
    }
    return actionFilters;
  };

  // Load card data when in card view or when filters change
  useEffect(() => {
    if (viewMode === 'cards') {
      loadActions();
    }
  }, [viewMode, searchTerm, activeFilters, refreshKey]);

  /**
   * Load actions created by current user
   */
  const loadActions = async () => {
    try {
      setCardLoading(true);
      setError(null);
      const actionFilters = getActionFilters();
      const data = await actionService.getActionsICreated(actionFilters);
      setActions(data);
      applyFiltersAndSort(data, searchTerm, activeFilters, sortBy);
    } catch (err: any) {
      console.error('Error loading actions I created:', err);
      setError(err.message || 'Failed to load actions');
    } finally {
      setCardLoading(false);
    }
  };
  
  /**
   * Apply filters and sorting to card data
   */
  const applyFiltersAndSort = (data: any[], search: string, filters: string[], sort: string) => {
    let filtered = [...data];
    
    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(action =>
        action.title.toLowerCase().includes(searchLower) ||
        (action.description && action.description.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply filters: priority (priority:*) and status
    const prioritySelected = filters.filter(f => f.startsWith('priority:')).map(f => f.replace('priority:', '').toUpperCase());
    const statusSelected = filters.filter(f => ['pending', 'in-progress', 'completed', 'overdue'].includes(f));
    if (prioritySelected.length > 0) {
      filtered = filtered.filter(a => prioritySelected.includes((a.priority || '').toUpperCase()));
    }
    if (statusSelected.length > 0) {
      filtered = filtered.filter(a => {
        if (statusSelected.includes('overdue')) {
          if (a.due_date && a.status !== 'COMPLETED' && a.status !== 'CANCELLED' && new Date(a.due_date) < new Date()) return true;
        }
        if (statusSelected.includes('pending') && a.status === 'PENDING') return true;
        if (statusSelected.includes('in-progress') && a.status === 'IN_PROGRESS') return true;
        if (statusSelected.includes('completed') && a.status === 'COMPLETED') return true;
        return false;
      });
    }
    
    // Apply sorting
    switch (sort) {
      case 'name':
        filtered.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'date-newest':
        filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      case 'priority-high': {
        const priorityOrder: Record<string, number> = { 'CRITICAL': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
        filtered.sort((a, b) => (priorityOrder[b.priority as string] || 0) - (priorityOrder[a.priority as string] || 0));
        break;
      }
      case 'relevance':
      default:
        // Overdue first, then by priority
        filtered.sort((a, b) => {
          const aOverdue = a.due_date && new Date(a.due_date) < new Date() && a.status !== 'COMPLETED';
          const bOverdue = b.due_date && new Date(b.due_date) < new Date() && b.status !== 'COMPLETED';
          if (aOverdue && !bOverdue) return -1;
          if (!aOverdue && bOverdue) return 1;
          const priorityOrder: Record<string, number> = { 'CRITICAL': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
          return (priorityOrder[b.priority as string] || 0) - (priorityOrder[a.priority as string] || 0);
        });
        break;
    }
    
    setFilteredCardData(filtered);
  };
  
  // Update filtered data when search, filters, or sort changes
  useEffect(() => {
    if (viewMode === 'cards' && actions.length > 0) {
      applyFiltersAndSort(actions, searchTerm, activeFilters, sortBy);
    }
  }, [searchTerm, activeFilters, sortBy, actions, viewMode]);
  
  const handleView = (item: any) => {
    navigate(`/actions/${item.id}`);
  };
  
  const handleEdit = (item: any) => {
    navigate(`/actions/${item.id}`);
  };
  
  const handleDelete = async (item: any) => {
    try {
      await actionService.deleteAction(item.id);
      refresh();
    } catch (error) {
      console.error('Error deleting action:', error);
      throw error;
    }
  };

  const handleDeleteWithConfirm = (item: any) => {
    if (skipDeleteConfirmation) {
      handleDelete(item);
    } else {
      setActionToDelete(item);
    }
  };

  const handleConfirmDeleteAction = async () => {
    if (!actionToDelete) return;
    await handleDelete(actionToDelete);
  };
  
  /**
   * Create filtered service for table view
   */
  const createFilteredService = () => {
    return {
      getAll: async (params: any = {}) => {
        const actionFilters = getActionFilters();
        // Merge with any additional params from table
        const combinedFilters = { ...actionFilters, ...params };
        const data = await actionService.getActionsICreated(combinedFilters);
        return {
          data,
          pagination: {
            currentPage: params.page || 1,
            totalPages: 1,
            totalItems: data.length,
            itemsPerPage: params.limit || data.length
          }
        };
      },
      getById: async (id: string) => {
        return await actionService.getActionById(id);
      },
      create: async (data: any) => {
        return await actionService.createAction(data);
      },
      update: async (id: string, data: any) => {
        return await actionService.updateAction(id, data);
      },
      delete: async (id: string) => {
        return await actionService.deleteAction(id);
      }
    };
  };

  const getActionFilterColorClass = (filter: { id: string; label: string }, isActive: boolean): string | undefined => {
    const id = String(filter.id || '').toLowerCase();
    const severityPart = id.startsWith('priority:') ? id.split(':')[1] || id : id;
    const tone = (activeClass: string, inactiveClass: string) => (isActive ? activeClass : inactiveClass);
    const isPriority = id.startsWith('priority:') && ['critical', 'high', 'medium', 'low'].includes(severityPart);
    const isStatus = ['pending', 'in-progress', 'completed', 'overdue'].includes(id);

    if (id === 'overdue') {
      return tone(
        'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-2 border-red-600 font-semibold',
        'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border border-red-400 hover:bg-red-50'
      );
    }
    if (isPriority) {
      if (severityPart === 'critical') {
        return tone(
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-red-700 font-semibold',
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-red-700 hover:bg-red-50'
        );
      }
      if (severityPart === 'high') {
        return tone(
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-rose-600 font-semibold',
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-rose-600 hover:bg-rose-50'
        );
      }
      if (severityPart === 'medium') {
        return tone(
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-amber-700 font-semibold',
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-amber-700 hover:bg-amber-50'
        );
      }
      return tone(
        'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-emerald-700 font-semibold',
        'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-emerald-700 hover:bg-emerald-50'
      );
    }
    if (isStatus) {
      if (id === 'completed') {
        return tone(
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-2 border-green-600 font-semibold',
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border border-green-400 hover:bg-green-50'
        );
      }
      if (id === 'in-progress') {
        return tone(
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-2 border-yellow-500 font-semibold',
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border border-yellow-300 hover:bg-yellow-50'
        );
      }
      if (id === 'pending') {
        return tone(
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-2 border-blue-500 font-semibold',
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border border-blue-300 hover:bg-blue-50'
        );
      }
    }
    return undefined;
  };

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="bg-[var(--color-surface-bg)] border-0 shadow-none p-4">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md text-[var(--color-error)] text-sm">
              {error}
            </div>
          )}

          {/* Filter Toolbar: search + sort + view switcher on one line; filters (Priority, Status) below */}
          <FilterToolbar
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                searchPlaceholder={t('actions.searchPlaceholder') || 'Search actions...'}
                quickFilterGroups={[
                  {
                    id: 'priority',
                    label: t('actions.filters.priority') || 'Priority',
                    filters: [
                      { id: 'priority:critical', label: 'Critical', value: 'priority:critical' },
                      { id: 'priority:high', label: 'High', value: 'priority:high' },
                      { id: 'priority:medium', label: 'Medium', value: 'priority:medium' },
                      { id: 'priority:low', label: 'Low', value: 'priority:low' }
                    ]
                  },
                  {
                    id: 'status',
                    label: t('actions.filters.status') || 'Status',
                    filters: [
                      { id: 'pending', label: 'Pending', value: 'pending' },
                      { id: 'in-progress', label: 'In Progress', value: 'in-progress' },
                      { id: 'completed', label: 'Completed', value: 'completed' },
                      { id: 'overdue', label: 'Overdue', value: 'overdue' }
                    ]
                  }
                ]}
                activeFilters={activeFilters}
                onFilterToggle={(filterId) => {
                  setActiveFilters(prev =>
                    prev.includes(filterId)
                      ? prev.filter(id => id !== filterId)
                      : [...prev, filterId]
                  );
                }}
                sortOptions={[
                  { id: 'relevance', label: 'Relevance', value: 'relevance' },
                  { id: 'name', label: 'Title (A-Z)', value: 'name' },
                  { id: 'date-newest', label: 'Date (Newest)', value: 'date-newest' },
                  { id: 'priority-high', label: 'Priority (High)', value: 'priority-high' }
                ]}
                sortValue={sortBy}
                onSortChange={setSortBy}
                filterColorResolver={getActionFilterColorClass}
                controlsAfterSort={
                  <ViewSwitcher
                    currentView={viewMode}
                    onViewChange={setViewMode}
                  />
                }
          />

          {viewMode === 'table' ? (
            <div className="mt-6">
              <FlexibleCRUDTable
                key={`${refreshKey}-${JSON.stringify(activeFilters)}-${searchTerm}`}
                entity={actionEntity}
                service={createFilteredService()}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={handleDelete}
                useConfirmDialog={true}
                skipDeleteConfirmation={skipDeleteConfirmation}
                onDeleteDontAskAgain={() => setSkipDeleteConfirmation(true)}
                searchPlaceholder={t('actions.searchPlaceholder') || 'Search actions...'}
                showPagination={true}
                showSearch={false} // Hide table's internal search since we have FilterToolbar
              />
            </div>
          ) : (
            <div>
              {cardLoading ? (
                <div className="text-center py-8 mt-6">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent-text)] mx-auto"></div>
                  <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mt-2">Loading...</p>
                </div>
              ) : filteredCardData.length === 0 ? (
                <div className="text-center py-8 mt-6">
                  <p className="text-sm text-[var(--color-secondary-text)] opacity-75">
                    {actions.length === 0 
                      ? (t('actions.noActions') || 'No actions found')
                      : 'No actions match your filters'}
                  </p>
                </div>
              ) : (
                <div className="mt-6">
                  <CardGrid columns={3}>
                    {filteredCardData.map((action) => (
                      <ActionCard
                        key={action.id}
                        action={action}
                        onEdit={(action) => navigate(`/actions/${action.id}`)}
                        onDelete={handleDeleteWithConfirm}
                        onClick={(id) => navigate(`/actions/${id}`)}
                      />
                    ))}
                  </CardGrid>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!actionToDelete}
        onClose={() => setActionToDelete(null)}
        onConfirm={handleConfirmDeleteAction}
        title={t('messages.confirmDeletePrompt') || 'Are you sure you want to delete?'}
        itemName={actionToDelete?.title}
        description={t('messages.deleteWarning') || 'This action cannot be undone.'}
        confirmLabel={t('common.delete') || 'Delete'}
        cancelLabel={t('common.cancel') || 'Cancel'}
        variant="danger"
        showDontAskAgain={true}
        dontAskAgainLabel={t('common.dontAskAgain') || "Don't ask again for this session"}
        onDontAskAgainChange={(checked) => checked && setSkipDeleteConfirmation(true)}
      />
    </div>
  );
};

